/*
 * Copyright © 2026. Legalbird eine Marke der Legalbird GmbH
 */

import React, { useState } from "react";
import Paper from "@mui/material/Paper";
import { dismissalCheckinStyle } from "./dismissalCheckinStyle";
import * as loginActions from "../../../store/login/actions";
import { useLocation, useNavigate } from "react-router";
import { getLpByLocation } from "../../../services/landingPagesList";
import Typography from "@mui/material/Typography";
import { FormProvider } from "../../../provider/Form/FormProvider";
import DismissalCheckinForm from "./DismissalCheckinForm";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { createResource, queryKeys } from "../../../services/reactQuery/reactQueryService";
import { Box } from "@mui/material";
import { getPartnerNameByIri, PARTNER_IRIS } from "../../../constants/partnerIris";
import type { PartnerIri } from "../../../constants/partnerIris";

type DismissalCheckinProps = {
  target?: string;
  idPrefix: string;
  color?: "default" | "grey";
  checkinHeading?: string;
  partner?: PartnerIri;
};

export default function DismissalCheckin({
  target = "/servicewelt",
  idPrefix,
  color = "default",
  checkinHeading = "Wir prüfen Ihre Kündigung kostenlos!",
  partner,
}: DismissalCheckinProps) {
  const [isLoading, setIsLoading] = useState(false);
  const [emailErrorText, setEmailErrorText] = useState("");
  const location = useLocation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const createMutation = useMutation({
    mutationFn: createResource,
    onSuccess: (data, variables) => queryClient.setQueryData(queryKeys.item(variables.uri, data.id), data),
  });

  const initialValues = {
    email: "",
    numberOfEmployees: "",
    telephone: "",
    givenName: null,
    familyName: null,
    gender: null,
  };

  const handleSubmit = async ({ values }: { values: Record<string, any> }) => {
    setIsLoading(true);
    const { from } = location.state || { from: { pathname: target } };
    const emailCheck = await loginActions.checkEmail(values.email.toLowerCase());
    if (emailCheck.hasError) {
      setIsLoading(false);
      setEmailErrorText(emailCheck.errorText);
      return;
    }

    const landingPage = getLpByLocation(location.pathname) || { keyword: location.pathname };
    const partnerName = getPartnerNameByIri(partner);
    const data = {
      email: values.email.toLowerCase(),
      telephone: values.telephone,
      gclid: localStorage.getItem("gclid"),
      person: {
        givenName: values.givenName,
        familyName: values.familyName,
        gender: values.gender,
      },
      prelead: {
        initialProduct: "settlement",
        numberOfEmployees: parseInt(values.numberOfEmployees, 10),
        landingPage: landingPage.keyword,
        partner: partnerName,
      },
    };

    await loginActions.register(data);
    await createMutation.mutateAsync({
      uri: "/settlements",
      data: {
        numberOfEmployees: parseInt(values.numberOfEmployees, 10),
        acquisitionPartner: partner,
        affiliateCode: localStorage.getItem("gclid"),
        landingPage: landingPage.keyword,
      },
    });
    await queryClient.invalidateQueries({
      queryKey: queryKeys.collection("/customers"),
    });
    navigate(from.pathname);
  };

  return (
    <Paper elevation={4} sx={{ ...dismissalCheckinStyle.formPaper, ...dismissalCheckinStyle[color] }}>
      <Box id={idPrefix + "-checkin"} sx={dismissalCheckinStyle.scrollToAnchor} />
      <Typography variant={"h4"} sx={dismissalCheckinStyle.heading}>
        {checkinHeading}
      </Typography>
      <FormProvider initialValues={initialValues} submitAction={handleSubmit}>
        <DismissalCheckinForm
          isLoading={isLoading}
          emailErrorText={emailErrorText}
          setEmailErrorText={setEmailErrorText}
          showPersonFields={partner === PARTNER_IRIS.WBS}
        />
      </FormProvider>
    </Paper>
  );
}
